/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchInput from '../../shared/chat-bar/search-input';
import { useTheme } from '../../theme-context';
import { useAppDispatch } from '../../app/hooks';
import { MEMBER_STATUS } from '../../utils/constants/member-status';
import {
    tryToAddActionForChat,
    tryToFetchSingleChat,
} from '../../store/brokers/partners/history-chats/historyChatsSlice';
import { tryToCreateChatMessage } from '../../store/brokers/partners/chats/ChatsSlice';
import TeamsModal from '../teams/TeamsModal';
import ReportIssuesModal from '../report-issue/ReportIssueModal';
import AiMessages from '../../shared/chat-messages/ai-messages';
import ClientMessages from '../../shared/chat-messages/client-messages';
import { useGetWidgetConfigQuery } from '../../store/widget/widgetConfigApi';
import { useGetCurrentUserQuery } from '../../store/user/userApi';


interface ChatDrawerProps {
    isOpen: boolean;
    action?: any;
    historyId?: string;
    triggerId: string;
    chatData: any;
    variant?: 'full' | 'widget';
    agentId?: string;
    clientId?: string;
    onSendSocketMessage?: (payload: { text: string }) => Promise<void>;
    onClose(): void;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
    isOpen,
    triggerId,
    historyId,
    chatData,
    action,
    variant = 'full',
    agentId = 'claire',
    clientId,
    onSendSocketMessage,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const hasAccessToken =
        typeof window !== 'undefined' &&
        !!window.localStorage.getItem('accessToken');
    const { data: currentUser } = useGetCurrentUserQuery(undefined, {
        skip: !hasAccessToken,
    });
    const resolvedClientId = clientId || currentUser?.data?.companyData?.sub;
    const { data: widgetConfig } = useGetWidgetConfigQuery(
        { agentId, clientId: resolvedClientId },
        { skip: !resolvedClientId },
    );
    const [chatId, setChatId] = useState<string>();
    const [isTyping, setIsTyping] = useState(false);
    const [allMessages, setAllMessages] = useState<any>();
    const [isWaiting, setIsWaiting] = useState<boolean>(false);
    const [showTeamsModal, setShowTeamsModal] = useState<boolean>(false);
    const [uploadedFiles, setUploadedFiles] = useState<any>();
    const [reportModal, setReportModal] = useState<{ show: boolean, messageId: string }>();
    const scrollableRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const hasStartedChat = useRef(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLanguageSelector] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('English');

    useEffect(() => {
        if (variant !== 'widget' || !isOpen) return;
        const body = document.body;
        const html = document.documentElement;
        const scrollY = window.scrollY;

        const prev = {
            overflow: body.style.overflow,
            touchAction: body.style.touchAction,
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            htmlOverscroll: html.style.overscrollBehavior,
        };

        body.style.overflow = 'hidden';
        body.style.touchAction = 'none';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        html.style.overscrollBehavior = 'none';
        return () => {
            const savedTop = body.style.top;
            body.style.overflow = prev.overflow;
            body.style.touchAction = prev.touchAction;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.left = prev.left;
            body.style.right = prev.right;
            body.style.width = prev.width;
            html.style.overscrollBehavior = prev.htmlOverscroll;

            const offset = savedTop ? Math.abs(parseInt(savedTop, 10)) : 0;
            if (offset) window.scrollTo(0, offset);
        };
    }, [isOpen, variant]);

    const primaryColor = useMemo(
        () => widgetConfig?.primaryColor || '#00330F',
        [widgetConfig],
    );
    const secondaryColor = useMemo(
        () => widgetConfig?.secondaryColor || '#F5F5F5',
        [widgetConfig],
    );
    const textColor = useMemo(
        () => widgetConfig?.textColor || '#FFFFFF',
        [widgetConfig],
    );

    const suggestedQuestions: string[] = useMemo(() => {
        if (Array.isArray(widgetConfig?.questions) && widgetConfig!.questions.length > 0) {
            return widgetConfig!.questions
                .slice()
                .sort((a: any, b: any) => (a.index ?? 0) - (b.index ?? 0))
                .map((q: any) => q.question)
                .filter((q: string | undefined) => !!q);
        }
        return [
            'Are there any available rooms?',
            'I am having some trouble with booking on the site',
            'I would like to book a room',
        ];
    }, [widgetConfig]);

    const agentName = useMemo(() => {
        const raw = widgetConfig?.agent || 'Leo';
        const s = String(raw).replace(/agent-|_/g, ' ').trim();
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Leo';
    }, [widgetConfig]);

    const welcomeMessage = useMemo(() => {
        const raw = widgetConfig?.welcomeMessage || 'Hi, I am {{agent_name}} an AI Assistant.';
        return String(raw).replaceAll('{{agent_name}}', agentName);
    }, [widgetConfig, agentName]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, section: string) => {
        const files = e.target.files;
        if (!files) return;

        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: [...(prev?.[section] || []), ...Array.from(files)],
        }));
    };

    const removeFile = (section: string, index: number) => {
        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: prev[section].filter((_: any, i: number) => i !== index),
        }));
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeout);
    }, [allMessages, isTyping, isWaiting]);

    useEffect(() => {
        if (variant !== 'widget' || !isWaiting) return;
        const lastMessage = allMessages?.[allMessages.length - 1];
        if (lastMessage?.agent) {
            setIsWaiting(false);
        }
    }, [allMessages, isWaiting, variant]);

    const onGettingChatHistory = async () => {
        if (historyId) {
            try {
                const response = await dispatch(tryToFetchSingleChat(historyId)).unwrap();
                const formatData = response?.data?.map((item: any) => {
                    if (item?.agent) return { agent: { ...item, ...item.agent } };
                    else return { user: { ...item, ...item.user } };
                });
                setAllMessages(formatData);
            } catch (err) {
                setAllMessages(chatData || undefined);
            }
        }
    };

    const onGetChatResponse = async (userMessage: any) => {
        const updated = allMessages ? [...allMessages, userMessage] : [userMessage];
        setAllMessages(updated);

        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);

        setIsWaiting(true);

        if (variant === 'widget' && onSendSocketMessage) {
            try {
                await onSendSocketMessage({
                    text: String(userMessage?.user?.message || ''),
                });
            } catch (error) {
                console.error('Failed to send socket message:', error);
                setAllMessages((prev: any) => [
                    ...(Array.isArray(prev) ? prev : []),
                    {
                        agent: {
                            message: 'Message not sent yet. Socket is not ready, please try again.',
                            createdAt: new Date().toISOString(),
                            files: [],
                        },
                    },
                ]);
                setIsWaiting(false);
            }
            return;
        }

        const payload: any = {
            topic: chatId,
            message: userMessage?.user?.message,
            files: userMessage?.user?.files || [],
            stage: allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.stage : undefined
        };

        try {
            const response = await dispatch(tryToCreateChatMessage(payload)).unwrap();
            const data = response;
            const agentReply = {
                agent: {
                    ...data || {},
                    message: data?.message,
                    files: data?.files || data?.files || [],
                    createdAt: data?.createdAt,
                    options: data?.options,
                    _id: response?.data?.agentMessageId || data?.agent?._id,
                },
            };
            setAllMessages([...updated, agentReply]);
        } catch (error) {
            console.error('Failed to send chat message:', error);
        } finally {
            setIsWaiting(false);
        }
    };

    const onStartChat = async () => {
        if (historyId || chatId) return;
        if (action) {
            setAllMessages([
                {
                    user: {
                        message: action?.text,
                        files: [],
                        createdAt: new Date(),
                    },
                },
            ])
        }
        setIsWaiting(true);
        try {
            if (action) {
                const data = {
                    actionSelected: action?.enum,
                    message: action?.text,
                    files: [],
                };
                const response = await dispatch(tryToCreateChatMessage(data)).unwrap();
                setAllMessages([
                    {
                        user: {
                            message: action?.text,
                            files: [],
                            createdAt: new Date(),
                        },
                    },
                    {
                        agent: {
                            ...response || {},
                            message: response?.message,
                            files: response?.files || [],
                            createdAt: new Date(),
                            _id: response?.metadata?.messageId,
                        },
                    },
                ]);
                setChatId(response?.topic);
            } else {
                const chatDataForm = { message: '' }
                const response = await dispatch(tryToCreateChatMessage(chatDataForm)).unwrap();
                setAllMessages([
                    {
                        agent: {
                            ...response || {},
                            message: response?.message,
                            files: response?.files || [],
                            createdAt: new Date(),
                            _id: response?.agentMessageId,
                        },
                    },
                ]);
                setChatId(response?.topic);
            }
        } catch (err) {
            console.error(err)
        } finally {
            setIsWaiting(false);
        }
    };

    useEffect(() => {
        if (historyId) {
            setChatId(historyId);
            onGettingChatHistory();
        }
    }, [historyId]);

    useEffect(() => {
        if (!historyId && !hasStartedChat.current && variant === 'full') {
            hasStartedChat.current = true;
            onStartChat();
        }
    }, [historyId, variant]);

    useEffect(() => {
        if (!chatData || !Array.isArray(chatData) || !!action) return;

        // Merge incoming socket/chat data against the latest local state.
        setAllMessages((prev: any) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            if (safePrev.length === 0) return chatData;

            const incoming = chatData.filter((item: any) => {
                const incomingAgentId = item?.agent?._id;
                const incomingUserKey = item?.user
                    ? `${item.user.message || ''}-${item.user.createdAt || ''}`
                    : undefined;

                return !safePrev.some((p: any) => {
                    const prevAgentId = p?.agent?._id;
                    const prevUserKey = p?.user
                        ? `${p.user.message || ''}-${p.user.createdAt || ''}`
                        : undefined;

                    if (incomingAgentId && prevAgentId) return incomingAgentId === prevAgentId;
                    if (incomingUserKey && prevUserKey) return incomingUserKey === prevUserKey;
                    return false;
                });
            });

            return incoming.length ? [...safePrev, ...incoming] : safePrev;
        });
    }, [chatData, action, historyId]);

    const onClickMessageAction = async (action: string, id: string) => {
        if (action === 'report') {
            setReportModal({
                show: true,
                messageId: id
            })
            return;
        }
        try {
            await dispatch(tryToAddActionForChat({ id, data: { action } })).unwrap();
            const formatAllMessages = allMessages?.map((item: any) => {
                if (item?.agent?._id === id) {
                    const key = action === 'like' ? 'liked' : action === 'dislike' ? 'disliked' : 'favorite';
					if (key === 'liked')
                    	return { agent: { ...item.agent, liked: !item.agent.liked, disliked: false } };
					else if (key === 'disliked')
						return { agent: { ...item.agent, disliked: !item.agent.disliked, liked: false } };
					return { agent: { ...item.agent, [key]: !item.agent[key] } };
                }
                return item;
            });
            setAllMessages(formatAllMessages);
        } catch (err) {
            console.error(err);
        }
    };

    const onClickLinks = (type: string) => {
        if (type === 'teams') setShowTeamsModal(true);
    };

    const onCloseLinksModals = () => setShowTeamsModal(false);

    const onSuccessfullyDeleted = () => {
        onGetChatResponse({
            user: {
                message: "I'm done with offboarding thank you",
                files: [],
            },
        });
    };

    const onClickOneOption = (option: any) => {
        onGetChatResponse({
            user: {
                message: `${option?.key ? option?.key + '. ' : ''}${option?.value}`,
                files: [],
            },
        });
    }

    const onSuccessReport = () => {
        onGetChatResponse({
            user: {
                message: '*Reported an issue.',
                files: [],
            },
        });
    }

    const onCloseReportModal = () => {
        setReportModal(undefined)
    }

    const handleMenuButtonClick = () => {
        if (variant === 'widget') {
            setMenuOpen((prev) => !prev);
        }
    };

    const handleCloseButtonClick = () => {
        onClose();
    };

    const renderMessages = () => (
        <>
            {(!allMessages || allMessages.length === 0) && (
                <div className='mb-4'>
                    <div className='rounded-lg p-4 shadow-sm h-full bg-white'>
                        <p className='text-base font-semibold text-black'>
                            Start a conversation with <span className='capitalize'>{agentName}</span>
                        </p>
                        <div className='mt-3 flex items-center gap-3'>
							<img src={`/assets/agents/${agentName.toLowerCase()}-agent.svg`} className='min-w-14 rounded-full' />
                            <div>
                                <p className='text-sm text-[#737376]'>{agentName}</p>
                                <p className='flex items-center gap-1 text-sm font-semibold text-emerald-500'>
                                    <span className='h-2 w-2 rounded-full bg-emerald-500' />
                                    Responds immediately
                                </p>
                            </div>
                        </div>
                        <p className='mt-3 text-sm text-gray-700'>{welcomeMessage}</p>
                        <p className='mt-4 text-sm text-[#737376]'>
                            Most common questions
                        </p>
                        <div className='mt-2 space-y-2'>
                            {suggestedQuestions.map((question) => (
                                <button
                                    key={question}
                                    type='button'
                                    onClick={() =>
                                        onGetChatResponse({
                                            user: {
                                                message: question,
                                                files: [],
                                                createdAt: new Date(),
                                            },
                                        })
                                    }
                                    className='flex w-full items-center gap-2 rounded-full p-3 py-2.5 text-left text-sm hover:opacity-90'
                                    style={{ backgroundColor: primaryColor, color: textColor }}
                                >
                                    <span className='inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] text-[#00330F]'>
										<img src='/svg/icons/send-msg.svg' alt='send' className='w-4 h-4' />
                                    </span>
                                    <span>{question}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {allMessages?.map((item: any, idx: number) => {
                const templateFiles = item?.agent?.stage === 'upload_template_files' ? allMessages && allMessages?.[idx + 1]?.user?.files || null : null;

                return (
                    <div key={idx}>
                        {item?.agent ? (
                            <div className='flex flex-row items-start'>
                                {(idx === 0 || !allMessages[idx - 1]?.agent?.message) && (
                                    <div className='mt-4 mr-2'>
                                        <img src={`/assets/agents/${agentName.toLowerCase()}-agent.svg`} className='min-w-10 rounded-full' />
                                    </div>
                                )}
                                <AiMessages
                                    text={item.agent.message}
                                    files={item.agent.files}
                                    isNew={(allMessages?.length - 1) === idx}
                                    data={item.agent}
                                    setIsTyping={setIsTyping}
                                    isTyping={isTyping && ((allMessages?.length - 1) === idx)}
                                    showActions
                                    considerDarkMode
                                    bubbleBgColor={secondaryColor}
                                    uploadedFiles={uploadedFiles}
                                    templateFiles={templateFiles}
                                    handleFileChange={handleFileChange}
                                    removeFile={removeFile}
                                    onClickAction={onClickMessageAction}
                                    onClickLinks={onClickLinks}
                                    onClickOptions={onClickOneOption}
                                />
                            </div>
                        ) : (
                            <ClientMessages
                                color='bg-primary-default'
                                bubbleBgColor={primaryColor}
                                text={item.user.message}
                                // time={item.user.createdAt}
                                considerDarkMode
                                stage={item?.user?.stage}
                                files={item.user.files}
                            />
                        )}
                    </div>
                );
            })}

            {isWaiting && (
                <div className='flex items-start gap-2 mt-3'>
                    <img src={`/assets/agents/${agentName.toLowerCase()}-agent.svg`} className='min-w-[35px] rounded-full' />
                    <div className='text-gray-400 py-2 px-3 rounded-xl max-w-[85%] text-sm animate-pulse'>
                        {agentId.charAt(0).toUpperCase() + agentId.slice(1)} is preparing your answer...
                    </div>
                </div>
            )}

            <div ref={bottomRef} />
        </>
    );

    const renderInputBar = () => (
        <SearchInput
            placeholder='Ask something'
            chatData={allMessages}
            chatId={chatId}
            setUploadedTemplateFiles={setUploadedFiles}
            uploadedTemplateFiles={uploadedFiles}
            considerDarkMode
            stage={allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.stage : undefined}
            options={allMessages && allMessages?.length > 0 ? allMessages?.[allMessages?.length - 1]?.agent?.options : undefined}
            onClickOptions={onClickOneOption}
            onSendingData={onGetChatResponse}
            isWaiting={isWaiting || isTyping}
        />
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    layoutId={triggerId}
                    className={variant === 'widget' ? 'fixed inset-0 z-40 flex items-end justify-center md:justify-end pointer-events-none' : 'relative w-full h-screen bg-[#F5F5F5] dark:bg-bgColor-darkMode flex flex-col'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                    {variant === 'full' && (
                        <>
                            <header className='flex m-auto items-center justify-between p-4 min-w-[90%] max-w-[90%]'>
                                <div></div>
                                <div className='flex flex-row items-center cursor-pointer hover:opacity-80' onClick={onClose}>
                                    <button className='mr-2 dark:text-primary-dark'>Close</button>
                                    <img
                                        src={theme === 'dark' ? '/assets/shared/close-icon-white.svg' : '/assets/shared/close-icon.svg'}
                                        className='object-contain w-[25px]'
                                    />
                                </div>
                            </header>

                            <div
                                ref={scrollableRef}
                                className='flex-1 overflow-y-auto px-4 py-2 max-w-[90%] min-w-[90%] mx-auto border-2 border-gray-200 bg-white dark:bg-[#3a3a3a] rounded-xl'
                            >
                                {renderMessages()}
                            </div>

                            <div className='sticky bottom-0 py-4 bg-[#F5F5F5] dark:bg-bgColor-darkMode'>
                                {renderInputBar()}
                            </div>
                        </>
                    )}

                    {variant === 'widget' && (
                        <div className='pointer-events-auto mr-0 w-full md:max-w-sm px-0 md:px-4 md:mr-10'>
                            <div
                                aria-hidden='true'
                                className='fixed inset-0 z-30 md:hidden'
                                style={{ backgroundColor: menuOpen ? primaryColor : '#F5F5F5' }}
                            />
                            <button
                                type='button'
                                aria-label='Close chat overlay'
                                onClick={onClose}
                                className='fixed inset-0 z-40 bg-transparent md:hidden'
                            />
                            <div 
								className='overflow-hidden overscroll-contain shadow-2xl flex flex-col md:rounded-lg fixed inset-0 z-50 w-full h-[100dvh] md:relative md:inset-auto md:w-[376px] md:h-[640px]'
								style={{ backgroundColor: menuOpen ? primaryColor : '#FFFFFF' }}
							>
								<div className=''>
									<div className='flex items-center justify-between px-4 py-3 md:rounded-md p-2 rounded-b-none' style={{ backgroundColor: primaryColor }}>
										<div className='flex items-center gap-2'>
											<div className='flex h-8 w-8 items-center justify-center rounded-full bg-white/10 overflow-hidden'>
												{widgetConfig?.logoUrl ? (
													<img
														src={widgetConfig.logoUrl}
														alt='Brand logo'
														className='h-full w-full object-contain'
													/>
												) : (
													<span className='text-xs text-white'>HW</span>
												)}
											</div>
										</div>
										<div className='flex items-center gap-1'>
											<button
												type='button'
												onClick={handleMenuButtonClick}
												className='flex h-7 w-7 items-center justify-center rounded-full text-white'
											>
												<img
													src={menuOpen ? '/svg/icons/hamburger-close.svg' : '/svg/icons/hamburger.svg'}
													alt='menu'
													className='w-6 h-6'
												/>
											</button>
											<button
												type='button'
												onClick={handleCloseButtonClick}
												className='flex h-7 w-7 items-center justify-center rounded-full text-white ml-1.5'
											>
												<img
													src='/svg/icons/close-chat.svg'
													alt='close chat'
													className='w-6 h-6'
												/>
											</button>
										</div>
									</div>
								</div>
                                

                                {menuOpen ? (
									<div className='flex-1 px-4 py-4 overflow-y-auto' style={{ backgroundColor: primaryColor }}>
										<div className='flex flex-col text-white text-base gap-4 mt-4'>
											
											<button type='button' className='flex items-center justify-end gap-2 hover:opacity-80' onClick={() => window.open('https://www.hotelworld.ai/privacy-policy', '_blank')}>
												<span>Privacy Policy</span>
												<img src='/svg/icons/privacy-policy.svg' className='w-6 h-6' />
											</button>
											
											{showLanguageSelector && (
												<div className='ml-auto w-full max-w-md rounded-lg bg-white px-4 pt-4 pb-2 text-gray-800 shadow-md mb-5'>
													<div className='mt-0'>
														<button
															type='button'
															onClick={() => setSelectedLanguage('English')}
															className='flex w-full items-center justify-between rounded-full border border-gray-200 bg-white px-4 py-2 text-base font-medium max-w-40 ml-auto'
														>
															<span>{selectedLanguage}</span>
															<span className='text-xl'>⌄</span>
														</button>
													</div>
													<p className='mt-2 text-[13px] text-gray-500 text-right'>
														This will change the language of this widget only!
													</p>
												</div>
											)}
										</div>
									</div>
								) : (
									<>
										<div className='flex-1 px-3 py-3 overflow-y-auto overscroll-contain bg-[#F5F5F5]' ref={scrollableRef}>
											{renderMessages()}
										</div>

										<div
                                            className='bg-white px-1 py-2'
                                            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
                                        >
											{renderInputBar()}
										</div>
									</>
								)}
                                
                            </div>
                        </div>
                    )}
                </motion.div>
            )}

            {showTeamsModal && (
                <TeamsModal
                    openModal={showTeamsModal}
                    status={MEMBER_STATUS.find((item: any) => item?.value === 'live')}
                    handleCloseModal={onCloseLinksModals}
                    SuccessfullyDeleted={onSuccessfullyDeleted}
                />
            )}
            {reportModal?.show &&
                <ReportIssuesModal
                    openModal={reportModal?.show}
                    messageId={reportModal?.messageId || ''}
                    chatId={chatId || ''}
                    onSuccessReport={onSuccessReport}
                    handleCloseModal={onCloseReportModal}
                />
            }
        </AnimatePresence>
    );
};

export default ChatDrawer;
